import Chart from 'chart.js/auto';
import { subscribe, getState } from '../state/store.js';
import { getHighestCharacterLevel } from '../utils/levels.js';
import { getCareerLengthDays, formatCareerLength } from '../utils/career.js';

const ctxPlayers = document.getElementById('chartPlayers');
const ctxStatus = document.getElementById('chartStatus');
const ctxClasses = document.getElementById('chartClasses');
const ctxRaces = document.getElementById('chartRaces');
const ctxAlignments = document.getElementById('chartAlignments');
const ctxLevels = document.getElementById('chartLevels');
const highlightsEl = document.getElementById('statsHighlights');
const leadersEl = document.getElementById('statsLeaders');

let charts = {};
let cachedData = {
  players: [],
  statuses: [],
  classes: [],
  races: [],
  alignments: [],
  levels: [],
  highlights: [],
  leaders: []
};

const palette = [
  '#a9812e',
  '#6b7860',
  '#8a1c2e',
  '#4e7a6d',
  '#c9a54a',
  '#7d5a8a',
  '#b8ae95',
  '#9c5b3c',
  '#5c6b3f',
  '#6b7f94',
  '#b06a3f',
  '#8f6f9e'
];

const normalizeStatLabel = (value) => {
  const label = String(value || '').trim();
  if (!label) return null;
  return label.toLowerCase() === 'unknown' ? null : label;
};

const isAliveCharacter = (character) => String(character.status || '').trim().toLowerCase() === 'alive';

const buildCounts = (rows, accessor) => {
  const map = new Map();
  rows.forEach((row) => {
    const key = normalizeStatLabel(accessor(row));
    if (!key) return;
    map.set(key, (map.get(key) || 0) + 1);
  });
  return Array.from(map.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 12);
};

const buildLevelHistogram = (rows) => {
  const map = new Map();
  rows.forEach((row) => {
    const level = getHighestCharacterLevel(row.level, NaN);
    if (Number.isFinite(level)) {
      map.set(level, (map.get(level) || 0) + 1);
    }
  });
  return Array.from(map.entries()).sort((a, b) => a[0] - b[0]);
};

const parseNumericValue = (value) => {
  const normalized = String(value ?? '').trim();
  if (!normalized) return null;
  if (!/^-?\d+(?:\.\d+)?$/.test(normalized)) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const parseCharacterLevelValue = (value) => {
  const level = getHighestCharacterLevel(value, NaN);
  return Number.isFinite(level) ? level : null;
};

const getExceptionalStrengthPercent = (value) => {
  const normalized = String(value ?? '').trim();
  if (!normalized) return null;

  const slashMatch = normalized.match(/^18\s*\/\s*(\d{1,3})$/i);
  if (slashMatch) {
    const rawPercent = slashMatch[1];
    const parsedPercent = rawPercent === '00' ? 100 : Number(rawPercent);
    if (Number.isFinite(parsedPercent) && parsedPercent >= 0 && parsedPercent <= 100) {
      return parsedPercent;
    }
    return null;
  }

  const decimalMatch = normalized.match(/^18\.(\d+)$/);
  if (decimalMatch) {
    const rawFraction = decimalMatch[1];
    if (/^0+$/.test(rawFraction)) return 100;
    return Number(rawFraction.slice(0, 2).padEnd(2, '0'));
  }

  return null;
};

const parseStrengthValue = (value) => {
  const exceptionalPercent = getExceptionalStrengthPercent(value);
  if (exceptionalPercent !== null) return 18 + exceptionalPercent / 101;
  return parseNumericValue(value);
};

const formatListSummary = (values = [], emptyText = 'No data') => {
  const unique = Array.from(new Set(values.filter(Boolean))).sort((a, b) => a.localeCompare(b));
  if (!unique.length) return { text: emptyText, title: '', empty: true };
  if (unique.length <= 2) return { text: unique.join(', '), title: unique.join(', '), empty: false };
  return {
    text: `${unique.slice(0, 2).join(', ')} +${unique.length - 2} more`,
    title: unique.join(', '),
    empty: false
  };
};

const formatRounded = (value) => {
  if (!Number.isFinite(value)) return '--';
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
};

const formatStrengthValue = (value) => {
  if (!Number.isFinite(value)) return '--';
  if (value <= 18 || value >= 19) return formatRounded(value);

  const percent = Math.round((value - 18) * 101);
  const percentLabel = percent === 100 ? '00' : String(percent).padStart(2, '0');
  return `18/${percentLabel}`;
};

const leaderDefinitions = [
  {
    key: 'str',
    label: 'Strongest',
    valueParser: parseStrengthValue,
    valueFormatter: formatStrengthValue
  },
  { key: 'dex', label: 'Most dexterous' },
  { key: 'con', label: 'Toughest' },
  { key: 'int', label: 'Smartest' },
  { key: 'wis', label: 'Wisest' },
  { key: 'cha', label: 'Most charismatic' },
  { key: 'level', label: 'Highest level', valueParser: parseCharacterLevelValue }
];

const buildExtremeCard = (
  rows,
  key,
  label,
  { mode = 'max', valueFormatter = String, valueParser = parseNumericValue } = {}
) => {
  let bestValue = null;
  let names = [];

  rows.forEach((row) => {
    const value = valueParser(row[key]);
    if (value === null) return;
    const name = row.name || 'Unnamed';

    const isBetter = bestValue === null || (mode === 'min' ? value < bestValue : value > bestValue);
    if (isBetter) {
      bestValue = value;
      names = [name];
      return;
    }

    if (value === bestValue) names.push(name);
  });

  const summary = formatListSummary(names);
  return {
    label,
    detail: summary.text,
    detailTitle: summary.title,
    valueText: bestValue === null ? '--' : valueFormatter(bestValue),
    empty: bestValue === null
  };
};

const buildModeCard = (rows, accessor, label) => {
  const counts = new Map();

  rows.forEach((row) => {
    const value = normalizeStatLabel(accessor(row));
    if (!value) return;
    counts.set(value, (counts.get(value) || 0) + 1);
  });

  let bestCount = null;
  let winners = [];
  counts.forEach((count, value) => {
    if (bestCount === null || count > bestCount) {
      bestCount = count;
      winners = [value];
      return;
    }
    if (count === bestCount) winners.push(value);
  });

  const summary = formatListSummary(winners);
  return {
    label,
    detail: summary.text,
    detailTitle: summary.title,
    valueText: bestCount === null ? '--' : String(bestCount),
    empty: bestCount === null
  };
};

const buildAverageCard = (rows, key, label, prefix = '', valueParser = parseNumericValue) => {
  const values = rows.map((row) => valueParser(row[key])).filter((value) => value !== null);
  if (!values.length) {
    return {
      label,
      detail: 'No data',
      detailTitle: '',
      valueText: '--',
      empty: true
    };
  }

  const average = values.reduce((sum, value) => sum + value, 0) / values.length;
  return {
    label,
    detail: `Across ${values.length} character${values.length === 1 ? '' : 's'}`,
    detailTitle: '',
    valueText: `${prefix}${formatRounded(average)}`,
    empty: false
  };
};

const buildCountValueCard = (
  rows,
  predicate,
  label,
  { detail = '', emptyDetail = 'No data', valueFormatter = String } = {}
) => {
  const matches = rows.filter(predicate);
  return {
    label,
    detail: matches.length ? detail : emptyDetail,
    detailTitle: '',
    valueText: valueFormatter(matches.length),
    empty: matches.length === 0
  };
};

const buildHighlightCards = (rows) => {
  const rowsWithCareerDays = rows.map((row) => ({ ...row, _careerLengthDays: getCareerLengthDays(row) }));

  const highlights = [
    buildExtremeCard(rows, 'age', 'Oldest', {
      valueFormatter: (value) => `${value} yr${value === 1 ? '' : 's'}`
    }),
    buildExtremeCard(rows, 'age', 'Youngest', {
      mode: 'min',
      valueFormatter: (value) => `${value} yr${value === 1 ? '' : 's'}`
    }),
    buildExtremeCard(rowsWithCareerDays, '_careerLengthDays', 'Longest Career', {
      valueFormatter: formatCareerLength
    }),
    buildAverageCard(rows, 'level', 'Average level', 'Lv ', parseCharacterLevelValue),
    buildModeCard(rows, (row) => row.alignment, 'Most common alignment'),
    buildModeCard(rows, (row) => row.current_location, 'Most common location'),
    buildModeCard(rows, (row) => row.birth_location, 'Most common birthplace'),
    buildCountValueCard(rows, isAliveCharacter, 'Living roster', {
      detail: 'Characters marked Alive',
      emptyDetail: 'Nobody is marked Alive'
    })
  ];

  return highlights;
};

const buildLeaderCard = (rows, key, label, options = {}) => {
  return buildExtremeCard(rows.filter(isAliveCharacter), key, label, options);
};

const updateCaches = () => {
  const { characters } = getState();
  cachedData = {
    players: buildCounts(characters, (c) => c.player),
    statuses: buildCounts(characters, (c) => c.status),
    classes: buildCounts(characters, (c) => c.class),
    races: buildCounts(characters, (c) => c.race),
    alignments: buildCounts(characters, (c) => c.alignment),
    levels: buildLevelHistogram(characters),
    highlights: buildHighlightCards(characters),
    leaders: leaderDefinitions.map(({ key, label, valueParser, valueFormatter }) =>
      buildLeaderCard(characters, key, label, { valueParser, valueFormatter })
    )
  };
};

const getThemeColors = () => {
  const cs = getComputedStyle(document.documentElement);
  const read = (name, fallback) => cs.getPropertyValue(name).trim() || fallback;
  return {
    text: read('--text', '#efece7'),
    grid: read('--divider', 'rgba(169,129,46,0.15)')
  };
};

const createChart = (ctx, type, labels, data) => {
  if (!ctx) return null;
  const existing = charts[ctx.id];
  if (existing) {
    existing.data.labels = labels;
    existing.data.datasets[0].data = data;
    existing.update();
    return existing;
  }
  const isBar = type === 'bar';
  const axisFont = { family: "'EB Garamond', Georgia, serif" };
  const themeColors = getThemeColors();
  const chart = new Chart(ctx, {
    type,
    data: {
      labels,
      datasets: [
        {
          label: '# of Characters',
          data,
          backgroundColor: palette,
          borderColor: '#14100e'
        }
      ]
    },
    options: {
      responsive: true,
      plugins: {
        legend: {
          display: !isBar,
          position: 'bottom',
          labels: { color: themeColors.text, font: { family: "'EB Garamond', Georgia, serif" } }
        }
      },
      scales: isBar
        ? {
            x: {
              ticks: { color: themeColors.text, font: axisFont },
              grid: { color: themeColors.grid },
              title: { display: true, text: 'Level', color: themeColors.text, font: axisFont }
            },
            y: {
              ticks: { color: themeColors.text, font: axisFont, precision: 0 },
              grid: { color: themeColors.grid },
              beginAtZero: true,
              title: { display: true, text: '# of Characters', color: themeColors.text, font: axisFont }
            }
          }
        : {}
    }
  });
  charts[ctx.id] = chart;
  return chart;
};

const destroyCharts = () => {
  Object.values(charts).forEach((chart) => chart?.destroy());
  charts = {};
};

const renderCharts = () => {
  createChart(
    ctxPlayers,
    'doughnut',
    cachedData.players.map(([label]) => label),
    cachedData.players.map(([, value]) => value)
  );
  createChart(
    ctxStatus,
    'doughnut',
    cachedData.statuses.map(([label]) => label),
    cachedData.statuses.map(([, value]) => value)
  );
  createChart(
    ctxClasses,
    'doughnut',
    cachedData.classes.map(([label]) => label),
    cachedData.classes.map(([, value]) => value)
  );
  createChart(
    ctxRaces,
    'doughnut',
    cachedData.races.map(([label]) => label),
    cachedData.races.map(([, value]) => value)
  );
  createChart(
    ctxAlignments,
    'doughnut',
    cachedData.alignments.map(([label]) => label),
    cachedData.alignments.map(([, value]) => value)
  );
  createChart(
    ctxLevels,
    'bar',
    cachedData.levels.map(([label]) => label),
    cachedData.levels.map(([, value]) => value)
  );
};

const renderSummaryCards = (container, cards = []) => {
  if (!container) return;

  container.replaceChildren();
  cards.forEach((entry) => {
    const card = document.createElement('article');
    card.className = 'stat-card';
    if (entry.empty) card.classList.add('is-empty');

    const heading = document.createElement('h4');
    heading.textContent = entry.label;

    const detail = document.createElement('p');
    detail.className = 'stat-card-detail';
    detail.textContent = entry.detail;
    if (entry.detailTitle) detail.title = entry.detailTitle;

    const value = document.createElement('span');
    value.className = 'stat-card-value';
    value.textContent = entry.valueText;

    card.append(heading, detail, value);
    container.append(card);
  });
};

const renderHighlights = () => renderSummaryCards(highlightsEl, cachedData.highlights);
const renderLeaders = () => renderSummaryCards(leadersEl, cachedData.leaders);

export const renderStatsTab = () => {
  updateCaches();
  renderCharts();
  renderHighlights();
  renderLeaders();
};

export const initStats = () => {
  updateCaches();
  subscribe((state) => {
    updateCaches();
    if (state.activeTab !== 'stats') return;
    renderCharts();
    renderHighlights();
    renderLeaders();
  });

  window.addEventListener('themechange', () => {
    destroyCharts();
    if (getState().activeTab !== 'stats') return;
    renderCharts();
  });
};
